import { useState } from 'react'
import { useAttendanceRecords } from '../hooks/useAttendanceRecords'
import type { EditAttendancePayload } from '../services/attendanceService'
import type { StaffAttendanceRecord } from '../types/hrmsAttendanceRecords.types'
import { AttendanceDetailsDrawer } from './AttendanceDetailsDrawer'
import { AttendanceFilters } from './AttendanceFilters'
import { AttendanceRecordsTable } from './AttendanceRecordsTable'
import { AttendanceTabHeader } from './AttendanceTabHeader'
import { EditAttendanceModal } from './EditAttendanceModal'
import { ImportAttendanceModal } from './ImportAttendanceModal'

interface AttendanceTabProps {
  selectedDate: string
  onDateChange: (val: string) => void
  selectedDepartment: string
  onDepartmentChange: (val: string) => void
  tabSlot?: React.ReactNode
}

export function AttendanceTab({
  selectedDate,
  onDateChange,
  selectedDepartment,
  onDepartmentChange,
  tabSlot,
}: AttendanceTabProps) {
  const state = useAttendanceRecords({
    selectedDate,
    selectedDepartment,
    onDateChange,
    onDepartmentChange,
  })

  const [editingRecord, setEditingRecord] = useState<StaffAttendanceRecord | null>(null)

  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    loading,
    summary,
    filteredCount,
    paginatedRecords,
    totalPages,
    from,
    to,
    selectedRecord,
    openDetails,
    closeDetails,
    applyApprovedRegularization,
    handleClearFilters,
    handleExport,
    isExporting,
    isImportModalOpen,
    openImportModal,
    closeImportModal,
    updateRecordAttendance,
    isSaving,
    departmentOptions,
    refetchRecords,
  } = state

  const handleSaveAttendance = async (
    recordId: string,
    updates: EditAttendancePayload
  ) => {
    const success = await updateRecordAttendance(recordId, updates)
    if (success) {
      setEditingRecord(null)
    }
  }

  return (
    <div className="space-y-6 min-w-0">
      <AttendanceTabHeader
        onExport={handleExport}
        onImport={openImportModal}
        isExporting={isExporting}
      />

      {tabSlot}

      <div className="min-w-0">
        <AttendanceFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          selectedDepartment={selectedDepartment}
          onDepartmentChange={onDepartmentChange}
          departmentOptions={departmentOptions}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onClearFilters={handleClearFilters}
          onExport={handleExport}
          isExporting={isExporting}
          summary={summary}
        />
      </div>

      <div className="min-w-0">
        <AttendanceRecordsTable
          records={paginatedRecords}
          filteredCount={filteredCount}
          currentPage={currentPage}
          totalPages={totalPages}
          from={from}
          to={to}
          selectedDate={selectedDate}
          loading={loading}
          onPageChange={setCurrentPage}
          onViewRecord={openDetails}
          onEditRecord={setEditingRecord}
          onExportDailySheet={handleExport}
        />
      </div>

      <AttendanceDetailsDrawer
        record={selectedRecord}
        selectedDate={selectedDate}
        onClose={closeDetails}
        onRegularizationApproved={applyApprovedRegularization}
      />

      <EditAttendanceModal
        isOpen={Boolean(editingRecord)}
        record={editingRecord}
        selectedDate={selectedDate}
        isSaving={isSaving}
        onClose={() => setEditingRecord(null)}
        onSave={handleSaveAttendance}
      />

      <ImportAttendanceModal
        open={Boolean(isImportModalOpen)}
        onClose={closeImportModal}
        onSuccessRefetch={refetchRecords}
      />
    </div>
  )
}
